<script setup lang="ts">
import { ArrowRight } from "lucide-vue-next";
import PublicPageLayout from "~/components/public/PublicPageLayout.vue";

const { t } = useI18n();
const localeRoute = useLocaleRoute();

const { canonicalUrl, currentLocale } = useLocalizedSeoLinks("/how-it-works");

useSeoMeta({
  title: () => t("public_pages.how_it_works.seo.title"),
  description: () => t("public_pages.how_it_works.seo.description"),
  robots: "index, follow",
  ogTitle: () => t("public_pages.how_it_works.seo.title"),
  ogDescription: () => t("public_pages.how_it_works.seo.description"),
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
});

const steps = ["first", "second", "third"] as const;
</script>

<template>
  <PublicPageLayout
    :eyebrow="$t('public_pages.common.senta')"
    :title="$t('public_pages.how_it_works.title')"
    :description="$t('public_pages.how_it_works.description')"
  >
    <div class="max-w-[1180px]">
      <section>
        <p class="text-xs font-bold uppercase tracking-[0.13em] text-primary">
          {{ $t("public_pages.how_it_works.clients.label") }}
        </p>
        <h2 class="mt-3 text-3xl font-bold tracking-tight text-app-text">
          {{ $t("public_pages.how_it_works.clients.title") }}
        </h2>
        <p class="mt-4 max-w-2xl leading-7 text-app-muted">
          {{ $t("public_pages.how_it_works.clients.description") }}
        </p>

        <ol class="mt-10 grid gap-10 md:grid-cols-3 md:gap-14">
          <li v-for="(step, index) in steps" :key="step" class="max-w-xs">
            <span class="text-xs font-bold tabular-nums text-primary"
              >0{{ index + 1 }}</span
            >
            <h3 class="mt-3 text-lg font-bold text-app-text">
              {{ $t(`public_pages.how_it_works.clients.steps.${step}.title`) }}
            </h3>
            <p class="mt-2 text-sm leading-6 text-app-muted">
              {{
                $t(
                  `public_pages.how_it_works.clients.steps.${step}.description`,
                )
              }}
            </p>
          </li>
        </ol>
      </section>

      <section class="mt-20 sm:mt-24">
        <p class="text-xs font-bold uppercase tracking-[0.13em] text-primary">
          {{ $t("public_pages.how_it_works.professionals.label") }}
        </p>
        <h2 class="mt-3 text-3xl font-bold tracking-tight text-app-text">
          {{ $t("public_pages.how_it_works.professionals.title") }}
        </h2>
        <p class="mt-4 max-w-2xl leading-7 text-app-muted">
          {{ $t("public_pages.how_it_works.professionals.description") }}
        </p>

        <ol class="mt-10 grid gap-10 md:grid-cols-3 md:gap-14">
          <li v-for="(step, index) in steps" :key="step" class="max-w-xs">
            <span class="text-xs font-bold tabular-nums text-primary"
              >0{{ index + 1 }}</span
            >
            <h3 class="mt-3 text-lg font-bold text-app-text">
              {{
                $t(
                  `public_pages.how_it_works.professionals.steps.${step}.title`,
                )
              }}
            </h3>
            <p class="mt-2 text-sm leading-6 text-app-muted">
              {{
                $t(
                  `public_pages.how_it_works.professionals.steps.${step}.description`,
                )
              }}
            </p>
          </li>
        </ol>
      </section>

      <div class="mt-20 flex flex-wrap gap-x-8 gap-y-3 sm:mt-24">
        <NuxtLink
          :to="localeRoute('/workers') || '/workers'"
          class="inline-flex items-center gap-2 text-sm font-bold text-primary animated-underline"
        >
          {{ $t("public_pages.how_it_works.next.find") }}
          <ArrowRight class="size-4" />
        </NuxtLink>
        <NuxtLink
          :to="localeRoute('/for-professionals') || '/for-professionals'"
          class="inline-flex items-center gap-2 text-sm font-bold text-primary animated-underline"
        >
          {{ $t("public_pages.how_it_works.next.join") }}
          <ArrowRight class="size-4" />
        </NuxtLink>
      </div>
    </div>
  </PublicPageLayout>
</template>
